import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { formatNumber, formatCurrency } from "@wrstudios/utils";
import { Modal } from "@wrstudios/components";
import { IconTrashCan } from "@wrstudios/icons";
import { formatCurrentInputValue } from "../../../utils/string";
import Loading from "../../common/Loading";
import { useLive } from "../LiveProvider";
import IconInfo from "../icons/IconInfo";
import IconPlus from "../icons/IconPlus";
import {
  Row,
  RowName,
  RowValue,
  Difference
} from "./styled/live-listing-details-aside";
import {
  AdjustedContainer,
  AdjustedStatus,
  AdjustedPriceArrow,
  AdjustedPrice,
  AdjustedPricePerSqft,
  Name,
  Value,
  Remove,
  Info,
  ModalContent,
  ModalText,
  Add
} from "./styled/live-listing-adjustments";

function LiveListingAdjustments({ listing }) {
  const { isAcreageReport, isReportOwnedByCurrentAgent } = useLive();
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showAddAdjustment, setShowAddAdjustment] = useState(false);
  const adjustedPriceDiffPercentage =
    ((listing.price - listing.adjustedPrice) / listing.price) * 100;

  useEffect(() => {
    if (!listing.adjustments.length) {
      setShowAddAdjustment(false);
    }
  }, [listing.adjustments]);

  return (
    <>
      {(!!listing.adjustments.length || showAddAdjustment) && (
        <>
          {!!listing.adjustedPrice && (
            <AdjustedContainer>
              <AdjustedStatus>
                <span>
                  Adjusted {listing.soldPrice ? "Sold" : "List"} Price
                </span>
                <Info onClick={() => setShowInfoModal(true)}>
                  <IconInfo />
                </Info>
              </AdjustedStatus>
              <AdjustedPrice>
                {listing.isPriceAdjusted && listing.isPriceAdjustedPositive && (
                  <AdjustedPriceArrow isPositive>&uarr;</AdjustedPriceArrow>
                )}
                {listing.isPriceAdjusted && listing.isPriceAdjustedNegative && (
                  <AdjustedPriceArrow>&darr;</AdjustedPriceArrow>
                )}
                {formatCurrency(listing.adjustedPrice)}
                {(!!listing.adjustedPricePerSqft ||
                  !!listing.adjustedPricePerAcre) && (
                  <AdjustedPricePerSqft>
                    {isAcreageReport ? (
                      <>{formatCurrency(listing.adjustedPricePerAcre)}/acre</>
                    ) : (
                      <>
                        {formatCurrency(listing.adjustedPricePerSqft)}
                        /sqft
                      </>
                    )}
                  </AdjustedPricePerSqft>
                )}
              </AdjustedPrice>
            </AdjustedContainer>
          )}
          {listing.adjustments.map((adjustment) => (
            <LiveListingAdjustment
              key={adjustment.id}
              listing={listing}
              adjustment={adjustment}
            />
          ))}
          {showAddAdjustment && <LiveListingAdjustmentAdd listing={listing} />}
          <AdjustedContainer>
            <Row>
              <RowName isTotal>
                Adjustments
                <Difference value={adjustedPriceDiffPercentage}>
                  {listing.isPriceAdjustedPositive && <> &uarr; </>}
                  {listing.isPriceAdjustedNegative && <> &darr; </>}
                  {Math.abs(adjustedPriceDiffPercentage) >= 1 && (
                    <>
                      {adjustedPriceDiffPercentage > 0
                        ? `${formatNumber(
                            adjustedPriceDiffPercentage,
                            "0,[0]"
                          )}%`
                        : adjustedPriceDiffPercentage < 0
                        ? `${formatNumber(
                            adjustedPriceDiffPercentage * -1,
                            "0,[0]"
                          )}%`
                        : ""}
                    </>
                  )}
                  {Math.abs(adjustedPriceDiffPercentage) < 1 && (
                    <>
                      {adjustedPriceDiffPercentage > 0
                        ? `${formatNumber(
                            adjustedPriceDiffPercentage,
                            "0[,][.]0[0]"
                          )}%`
                        : adjustedPriceDiffPercentage < 0
                        ? `${formatNumber(
                            adjustedPriceDiffPercentage * -1,
                            "0[,][.]0[0]"
                          )}%`
                        : ""}
                    </>
                  )}
                </Difference>
              </RowName>
              <RowValue
                isTotal
                isReportOwnedByCurrentAgent={isReportOwnedByCurrentAgent}>
                {!!listing.adjustmentsTotal
                  ? formatCurrency(listing.adjustmentsTotal)
                  : "-"}
              </RowValue>
            </Row>
          </AdjustedContainer>
        </>
      )}
      {isReportOwnedByCurrentAgent && !showAddAdjustment && (
        <AdjustedContainer noPad>
          <Row>
            <Add onClick={() => setShowAddAdjustment(true)}>
              <IconPlus /> Add Adjustments
            </Add>
          </Row>
        </AdjustedContainer>
      )}
      {showInfoModal && (
        <Modal onClose={() => setShowInfoModal(false)}>
          <ModalContent>
            <Modal.Header>
              Adjusted Price
              <Modal.Close />
            </Modal.Header>
            <Modal.Body>
              <ModalText>
                When conducting a comparative market analysis to determine the
                current market value of a home, a real estate agent or appraiser
                may use price adjustments to arrive at a more accurate home
                value. Price adjustments account for the differences between the
                subject home and comparable homes.
              </ModalText>
            </Modal.Body>
          </ModalContent>
        </Modal>
      )}
    </>
  );
}

function LiveListingAdjustment({ listing, adjustment }) {
  const {
    isReportOwnedByCurrentAgent,
    updateListingAdjustment,
    removeListingAdjustment
  } = useLive();
  const [name, setName] = useState(adjustment.name || "");
  const [value, setValue] = useState(adjustment.value || "");
  const formattedValue = formatCurrentInputValue(value);
  const isFake = String(adjustment.id).includes("-");

  if (!isReportOwnedByCurrentAgent) {
    return (
      <AdjustedContainer>
        <Row>
          <RowName>{name}</RowName>
          <RowValue>{formattedValue}</RowValue>
        </Row>
      </AdjustedContainer>
    );
  }

  return (
    <AdjustedContainer noPad>
      <Row>
        <Name
          placeholder="Name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            updateListingAdjustment({
              listingRecordId: listing.recordId,
              adjustmentId: adjustment.id,
              name: e.target.value,
              value: formatNumber(value, "0[.]0")
            });
          }}
        />
        <Value
          placeholder="$0"
          value={formattedValue}
          onChange={(e) => {
            setValue(e.target.value);
            updateListingAdjustment({
              listingRecordId: listing.recordId,
              adjustmentId: adjustment.id,
              name,
              value: formatNumber(e.target.value, "0[.]0")
            });
          }}
        />
        {isReportOwnedByCurrentAgent && (
          <Remove
            as={isFake ? "div" : "button"}
            onClick={
              isFake
                ? null
                : () => {
                    removeListingAdjustment({
                      listingRecordId: listing.recordId,
                      adjustmentId: adjustment.id
                    });
                  }
            }>
            {isFake && <Loading />}
            {!isFake && <IconTrashCan />}
          </Remove>
        )}
      </Row>
    </AdjustedContainer>
  );
}

LiveListingAdjustment.propTypes = {
  listing: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
  }).isRequired,
  adjustment: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  })
};

function LiveListingAdjustmentAdd({ listing }) {
  const { addListingAdjustment } = useLive();
  const nameRef = useRef();
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const formattedValue = formatCurrentInputValue(value);

  const handleAdd = () => {
    addListingAdjustment({
      listingRecordId: listing.recordId,
      name,
      value: formatNumber(value, "0[.]0")
    });
    setName("");
    setValue("");
    setTimeout(() => {
      nameRef.current.focus();
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && name) {
      handleAdd();
    }
  };

  return (
    <AdjustedContainer noPad>
      <Row>
        <Name
          autoFocus
          ref={nameRef}
          placeholder="Name"
          value={name}
          onKeyDown={handleKeyDown}
          onChange={async (e) => setName(e.target.value)}
        />
        <Value
          isAdd
          placeholder="$0"
          value={formattedValue}
          onKeyDown={(e) => {
            if (e.key === "Tab" && name && e.target.value) {
              handleAdd();
            }
            handleKeyDown(e);
          }}
          onChange={(e) => setValue(e.target.value)}
        />
      </Row>
    </AdjustedContainer>
  );
}

export default LiveListingAdjustments;
